import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { ClassificationSource, ConfidenceBand, WorkItem } from '../api/workItems';
import { bestGuessWhy, isBestGuessPlacement } from '../utils/classificationBestGuess';
import { isSuspiciousWorkItemDisplay } from '../utils/classificationSuspicion';
import { isManualClassificationNeutral, trustPanelChipSx } from './WorkItemTrustStrip';

const CLASSIFICATION_SOURCE_LABELS: Record<ClassificationSource, string> = {
    heuristic: 'Heuristic rules',
    ai_enrichment: 'AI enrichment',
    user_override: 'Your correction',
    bulk_user_approved: 'Bulk-approved change',
    migration: 'Migration',
    system_default: 'System default',
    unknown: 'Unknown',
};

const CONFIDENCE_BAND_LABELS: Record<ConfidenceBand, string> = {
    none: 'No confidence',
    zero: 'Zero confidence',
    low: 'Low confidence',
    medium: 'Medium confidence',
    high: 'High confidence',
};

function sourceLabel(source: WorkItem['classification_source']): string {
    if (!source) return CLASSIFICATION_SOURCE_LABELS['unknown'];
    return CLASSIFICATION_SOURCE_LABELS[source] ?? CLASSIFICATION_SOURCE_LABELS['unknown'];
}

function confidenceBandLabel(band: WorkItem['confidence_band']): string | null {
    if (!band || band === 'none') return null;
    return CONFIDENCE_BAND_LABELS[band] ?? null;
}

function formatConfidencePercent(confidenceNumeric: WorkItem['confidence_numeric']): string | null {
    if (confidenceNumeric == null || !Number.isFinite(confidenceNumeric)) {
        return null;
    }
    const bounded = Math.min(1, Math.max(0, confidenceNumeric));
    return `${Math.round(bounded * 100)}%`;
}

/** Story 7.2: omit band labels when canonical numeric confidence is unavailable. */
function hasUsableConfidenceNumeric(confidenceNumeric: WorkItem['confidence_numeric']): boolean {
    return (
        typeof confidenceNumeric === 'number' && Number.isFinite(confidenceNumeric)
    );
}

function conservativeClampCue(item: WorkItem): { reason: string; routedAt?: string } | null {
    const cqRaw = item.meta?.classification_quality;
    if (!cqRaw || typeof cqRaw !== 'object') {
        return null;
    }
    const cq = cqRaw as Record<string, unknown>;
    const reason =
        typeof cq.conservative_route_reason === 'string' ? cq.conservative_route_reason.trim() : '';
    if (!reason) return null;
    const routedAt =
        typeof cq.conservative_routed_at === 'string' ? cq.conservative_routed_at.trim() : '';
    return { reason, routedAt: routedAt || undefined };
}

function formatClampReasonSentence(reason: string): string {
    return reason.replace(/_/g, ' ');
}

function trimmedEvidence(item: WorkItem): string {
    return typeof item.evidence_summary === 'string' ? item.evidence_summary.trim() : '';
}

const WorkItemClassificationPanel: React.FC<{ item: WorkItem; suppressSuspicionCue?: boolean }> = ({
    item,
    suppressSuspicionCue = false,
}) => {
    const evidence = trimmedEvidence(item);
    const numericOk = hasUsableConfidenceNumeric(item.confidence_numeric);
    const confidencePercent = formatConfidencePercent(item.confidence_numeric);
    const bandLabel =
        numericOk && item.confidence_band ? confidenceBandLabel(item.confidence_band) : null;
    const bestGuess = isBestGuessPlacement(item);
    const suspicion = !bestGuess && isSuspiciousWorkItemDisplay(item) && !isManualClassificationNeutral(item);
    const clampCue = bestGuess ? null : conservativeClampCue(item);

    return (
        <Box
            component="section"
            data-testid="WB_CLASSIFICATION_PANEL"
            aria-labelledby="work-item-classification-heading"
            sx={{
                px: 3,
                py: 1.5,
                borderBottom: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg-primary)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FontAwesomeIcon icon={faCircleInfo} className="text-[14px] shrink-0 opacity-80" aria-hidden />
                <Typography
                    id="work-item-classification-heading"
                    component="h2"
                    variant="subtitle2"
                    sx={{ color: 'var(--color-text-primary)', fontWeight: 700 }}
                >
                    Classification
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        size="small"
                        label={sourceLabel(item.classification_source)}
                        sx={trustPanelChipSx}
                        variant="outlined"
                    />
                    {bestGuess ? null : confidencePercent ? (
                        <Chip
                            size="small"
                            label={confidencePercent}
                            sx={{
                                height: 24,
                                fontWeight: 700,
                                bgcolor: 'rgba(var(--color-accent-rgb), 0.08)',
                                color: 'var(--color-accent-text)',
                                border: '1px solid rgba(var(--color-accent-rgb), 0.24)',
                            }}
                            variant="outlined"
                        />
                    ) : (
                        <Typography
                            variant="caption"
                            sx={{ alignSelf: 'center', color: 'var(--color-text-secondary)', fontWeight: 600 }}
                        >
                            No confidence available
                        </Typography>
                    )}
                    {!bestGuess && bandLabel && (
                        <Typography
                            variant="caption"
                            sx={{ alignSelf: 'center', color: 'var(--color-text-secondary)', fontWeight: 600 }}
                        >
                            {bandLabel}
                        </Typography>
                    )}
                </Box>

                {bestGuess && (
                    <Alert
                        data-testid="WB_TRUST_BEST_GUESS_WHY"
                        severity="info"
                        icon={<FontAwesomeIcon icon={faCircleInfo} />}
                        sx={{
                            py: 0.5,
                            borderRadius: 1,
                            '& .MuiAlert-message': { py: 0.25 },
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {bestGuessWhy(item)}
                        </Typography>
                    </Alert>
                )}

                {clampCue && (
                    <Alert
                        severity="info"
                        icon={<FontAwesomeIcon icon={faCircleInfo} />}
                        sx={{
                            py: 0.5,
                            borderRadius: 1,
                            '& .MuiAlert-message': { py: 0.25 },
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Routed conservatively · {formatClampReasonSentence(clampCue.reason)}
                        </Typography>
                        {clampCue.routedAt ? (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                                {clampCue.routedAt}
                            </Typography>
                        ) : null}
                    </Alert>
                )}

                {suspicion && !suppressSuspicionCue ? (
                    <Alert
                        data-testid="WB_CLASSIFICATION_SUSPICION_CUE"
                        severity="warning"
                        icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                        sx={{
                            py: 0.5,
                            borderRadius: 1,
                            '& .MuiAlert-message': { py: 0.25 },
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Classification looks uncertain for this domain — signals may be incomplete or weak.
                            Review when convenient.
                        </Typography>
                    </Alert>
                ) : null}

                {evidence ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--color-text-secondary)',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {evidence}
                    </Typography>
                ) : !suspicion ? (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                        No evidence summary retained.
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
};

export default WorkItemClassificationPanel;
